import { Injectable } from "@angular/core";
import { Store, Action, select } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Company } from "../models/company.model";
import { TranslationService } from "@cemex-core/angular-localization-v7";
import { Observable, from, of } from "rxjs";
import { filter, tap, map, withLatestFrom, switchMap } from "rxjs/operators";

import { CoreService } from "./../services/core.service";
import * as fromCompanyActions from "./../actions/company.action";
import * as fromCore from "./../reducers/index";
import * as fromFinancialDocumentsAction from "./../../financial-documents/actions/documents.action";
import * as fromFinancialDocumentsActionV2 from "../../financial-documents-v2/actions/documents.action";
import { SharedDataService } from "./../../services/sharedData.service";
import { AppConfigurationService } from "../../../cemexng2components/components/app-configuration/services/ipc-app-configuration.service";

@Injectable()
export class CompanyEffect {
  constructor(
    private store$: Store<fromCore.State>,
    private actions$: Actions,
    private core: CoreService,
    private translation: TranslationService,
    private sharedData: SharedDataService,
    private features: AppConfigurationService
  ) {
    //
  }

  fetchCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType<fromCompanyActions.FetchCompanies>(fromCompanyActions.ActionTypes.FETCH_COMPANIES),
      withLatestFrom(
        this.store$.pipe(
          select(fromCore.getLegalEntity),
          filter((legalEntity) => legalEntity?.legalEntityTypeCode !== undefined || legalEntity?.countryCode !== undefined )
        )
      ),
      switchMap(([action, legalEntity]) => {
        return this.core.getLegalEntityByCustomer(legalEntity.legalEntityTypeCode, legalEntity.countryCode).pipe(
          map((companies: any[]) => {
            if (this.features.isEnabled("ipc-company-selector.version-3")) {
              companies = companies.map((x) => {
                let data: Company = {
                  companyCode: x.companyCode,
                  companyDesc: x.companyDesc,
                  countryCode: x.countryCode,
                  divisionCode: x.divisionCode,
                  paymentAllowed: x.paymentAllowed,
                  applicationCode: undefined,
                };

                if (x.applications && x.applications.length > 0) {
                  data.applicationCode = x.applications[0].applicationCode;
                }

                return data;
              });
            }

            if (this.features.isEnabled("dashboard.pagosenlinea-enabled")) {
              const dominio = this.features.getConfiguration('dashboard.pagosenlinea-enabled').options.dominio;
              const companiesInclude = this.features.getConfiguration('dashboard.pagosenlinea-enabled').options.companies;
              let filteredCompanies: any[] = [];

              if (window.location.hostname.includes(dominio)) {
                companies.map((x) => {
                  if (companiesInclude.includes(x.companyCode)) {
                    filteredCompanies.push(x);
                  }
                });
              }else {
                filteredCompanies = companies;
              }

              return new fromCompanyActions.FetchCompaniesSuccess(filteredCompanies);
            }else {
              return new fromCompanyActions.FetchCompaniesSuccess(companies);
            }
          })
        );
      })
    )
  );

  // Effect using for old Financial tables
  selectCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType<fromCompanyActions.SelectCompany>(fromCompanyActions.ActionTypes.SELECT_COMPANY),
      map((action) => {
        this.sharedData.checkedFinancialDocuments = [];
        return new fromFinancialDocumentsAction.LoadFinancialDocuments();
      })
    )
  );

  // Effect using for Financial table v3
  selectCompanyV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType<fromCompanyActions.SelectCompany>(fromCompanyActions.ActionTypes.SELECT_COMPANY_V2),
      map((action) => {
        this.sharedData.checkedFinancialDocuments = [];
        return new fromFinancialDocumentsActionV2.LoadFinancialDocuments();
      })
    )
  );

  fetchCompaniesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<fromCompanyActions.FetchCompaniesFailure>(fromCompanyActions.ActionTypes.FETCH_COMPANIES_FAILURE),
        tap((err) => console.error(err))
      ),
    { dispatch: false }
  );

  fetchCompaniesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<fromCompanyActions.FetchCompaniesSuccess>(fromCompanyActions.ActionTypes.FETCH_COMPANIES_SUCCESS),
      map((action) => action.payload),
      withLatestFrom(
        this.store$.pipe(
          select(fromCore.getLegalEntity),
          filter((legalEntity) => legalEntity !== undefined)
        )
      ),
      switchMap(([companies, legalEntity]) => {
        if (!companies || companies.length === 0) {
          return of(new fromCompanyActions.SelectCompany(undefined));
        }
        if (companies.length === 1) {
          return of(new fromCompanyActions.SelectCompany(companies[0].companyCode));
        } else {

          let selectedCompany: Company = {
              companyCode: "0",
              companyDesc: this.translation.pt("view.invoicepayment.dashboard.all.cemex.title"),
              countryCode: (legalEntity.countryCode && legalEntity.countryCode.trim()) || "",
              divisionCode: 0,
              applicationCode: "INVOICE_PAYMENTS",
          };

          if (this.features.isEnabled("dashboard.pagosenlinea-enabled")) {
            const dominio = this.features.getConfiguration('dashboard.pagosenlinea-enabled').options.dominio;
            if (window.location.hostname.includes(dominio)) {
              // selectedCompany.companyDesc = this.translation.pt("view.invoicepayment.dashboard.all.cemex.title").replace(" CEMEX", "");
              return of(new fromCompanyActions.SelectCompany(companies[0].companyCode));
            }
          };

          return from([
            new fromCompanyActions.AddCompany(selectedCompany),
            new fromCompanyActions.SelectCompany(selectedCompany.companyCode),
          ]);
        }
      })
    )
  );
}
